import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  CompareBars,
  EmptyState,
  Field,
  Inspector,
  Metric,
  Page,
  QueryState,
  Select,
  Stack,
  StatStrip,
  TrendLine,
  DashboardIcon,
  TrendIcon,
  FilterIcon,
  GlobeIcon,
  CheckCircleIcon,
  ConnectorIcon,
  type CompareGroup,
} from '@integra/ui';
import {
  useAssets,
  useCampaigns,
  useCollectAnalytics,
  useConnectorConfigs,
  useDashboard,
  useDestinations,
  useSiteAnalytics,
  useUsage,
} from '../api/hooks.js';
import type {
  MetricRollup,
  MetricSegmentFigures,
  MetricSeriesPoint,
  SourceMetricRollup,
} from '../api/types.js';

/**
 * Performance (§20.5, §2.7). A focused analytics surface: pick what to report on,
 * and see what each channel MEASURED kept strictly apart from what INTEGRA
 * ESTIMATED. The two are never summed and an absent measurement is stated in
 * words, not drawn as a zero. Command-centre concerns (needs, setup, quick
 * actions) live on Home — this page is only the numbers.
 */

type Scope = 'campaigns' | 'channels' | 'assets';

const SCOPES: { value: Scope; label: string }[] = [
  { value: 'campaigns', label: 'Campaign' },
  { value: 'channels', label: 'Channel' },
  { value: 'assets', label: 'Asset' },
];

function readString(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/** True when a segment carries any figure worth showing. */
function hasFigures(figures: MetricSegmentFigures): boolean {
  return figures.measured !== null || figures.estimated !== null;
}

/**
 * One metric as a measured-vs-estimated comparison (§2.7). The pair is never
 * reconciled into one figure, the bars keep measured and estimated visually
 * apart, and a null reading is shown as "no data" rather than a zero bar. When
 * paid distribution exists the metric also breaks out organic vs paid — earned
 * and bought kept separate so neither is mistaken for the other.
 */
function MetricRollupCard({
  rollup,
  series,
  onInspect,
}: {
  rollup: MetricRollup;
  series: MetricSeriesPoint[];
  onInspect: () => void;
}) {
  const groups: CompareGroup[] = [
    { label: 'Total', measured: rollup.measured, estimated: rollup.estimated },
  ];
  if (hasFigures(rollup.paid)) {
    groups.push(
      { label: 'Organic', measured: rollup.organic.measured, estimated: rollup.organic.estimated },
      { label: 'Paid', measured: rollup.paid.measured, estimated: rollup.paid.estimated },
    );
  }
  const trend = series
    .filter((p) => p.metric === rollup.metric)
    .map((p) => ({ bucket: p.bucket, measured: p.measured, estimated: p.estimated }));
  return (
    <Card
      title={rollup.metric.replace(/_/g, ' ')}
      actions={
        <button
          type="button"
          onClick={onInspect}
          className="rounded-md px-2 py-1 text-xs font-semibold text-gold-text hover:bg-surface-sunken"
        >
          Inspect
        </button>
      }
    >
      <Stack gap="md">
        {trend.length >= 2 && (
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              Daily trend
            </p>
            <TrendLine
              points={trend}
              format={formatNumber}
              label={`${rollup.metric.replace(/_/g, ' ')} daily trend`}
            />
          </div>
        )}
        <CompareBars groups={groups} format={formatNumber} />
        <Cluster gap="sm" align="center">
          {rollup.measuredSources.length > 0 ? (
            <>
              <span className="text-xs text-ink-muted">Measured by</span>
              {rollup.measuredSources.map((source) => (
                <Badge key={source} tone="success" dot>
                  {source}
                </Badge>
              ))}
            </>
          ) : (
            <span className="text-xs text-ink-muted">No channel reported this metric.</span>
          )}
          {rollup.note !== null && <span className="text-xs text-ink-muted">· {rollup.note}</span>}
        </Cluster>
      </Stack>
    </Card>
  );
}

/** Human label for a source connector id used in the site-analytics section. */
const SOURCE_LABELS: Record<string, string> = {
  'google-analytics': 'Google Analytics 4',
};
function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

/**
 * One source-level figure — a GA4 property's own measurement — shown with its
 * source and, when present, its estimate kept strictly apart (§2.7). This is
 * website/audience data, deliberately NOT blended with the per-channel cards
 * above: a site session is not a channel post's measurement.
 */
function SourceMetricCard({ rollup }: { rollup: SourceMetricRollup }) {
  return (
    <Card title={rollup.metric.replace(/_/g, ' ')}>
      <Stack gap="md">
        <CompareBars
          groups={[{ label: 'Site', measured: rollup.measured, estimated: rollup.estimated }]}
          format={formatNumber}
        />
        <Cluster gap="sm" align="center">
          <span className="text-xs text-ink-muted">Measured by</span>
          <Badge tone="success" dot>
            {sourceLabel(rollup.source)}
          </Badge>
          {rollup.externalId !== null && (
            <span className="text-xs text-ink-muted">property {rollup.externalId}</span>
          )}
          {rollup.note !== null && <span className="text-xs text-ink-muted">· {rollup.note}</span>}
        </Cluster>
      </Stack>
    </Card>
  );
}

export function DashboardPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();

  const [scope, setScope] = useState<Scope>('campaigns');
  const [entityId, setEntityId] = useState('');
  const [inspecting, setInspecting] = useState<MetricRollup | null>(null);

  const campaigns = useCampaigns(workspaceId);
  const destinations = useDestinations(workspaceId);
  const assets = useAssets(workspaceId);
  const configs = useConnectorConfigs(workspaceId);

  const dashboard = useDashboard(workspaceId, scope, entityId);
  const site = useSiteAnalytics(workspaceId);
  const usage = useUsage(workspaceId);
  const collect = useCollectAnalytics(workspaceId);

  const campaignList = campaigns.data?.data ?? [];
  const channelList = destinations.data?.data ?? [];
  const totalMetered = (usage.data?.usage ?? []).reduce((sum, u) => sum + u.quantity, 0);
  const usageWindow = usage.data?.window;

  const configName = (id: string): string =>
    configs.data?.data.find((c) => c.id === id)?.displayName ?? id;

  const options =
    scope === 'campaigns'
      ? campaignList.map((campaign) => ({ id: campaign.id, label: campaign.name }))
      : scope === 'channels'
        ? channelList.map((destination) => ({
            id: destination.id,
            label: `${configName(destination.connectorConfigId)} (${destination.status})`,
          }))
        : (assets.data?.data.map((asset) => ({
            id: asset.id,
            label: readString(asset.data, 'title') ?? asset.id,
          })) ?? []);

  const metrics = dashboard.data?.metrics ?? [];
  const siteMetrics = site.data?.metrics ?? [];
  const collected = collect.data;

  return (
    <Page
      title="Performance"
      icon={DashboardIcon}
      description="See what each channel actually measured, kept strictly separate from what INTEGRA estimated."
      stats={
        totalMetered > 0 ? (
          <StatStrip
            stats={[
              { icon: TrendIcon, label: 'Metered activity', value: totalMetered, tone: 'accent' },
              { icon: DashboardIcon, label: 'Metrics', value: metrics.length },
            ]}
          />
        ) : undefined
      }
      actions={
        <Button variant="secondary" onClick={() => collect.mutate()} loading={collect.isPending}>
          Collect now
        </Button>
      }
    >
      <Stack gap="lg">
        {/* Window summary — one honest KPI of what's been metered. */}
        <div className="max-w-xs">
          <Metric
            label="Metered activity"
            value={formatNumber(totalMetered)}
            caption={`Publishes, renders & API calls${
              usageWindow !== undefined
                ? ` · ${new Date(usageWindow.from).toLocaleDateString()} – ${new Date(usageWindow.to).toLocaleDateString()}`
                : ''
            }`}
          />
        </div>
        {collect.error instanceof Error && (
          <p className="text-xs text-state-error">{collect.error.message}</p>
        )}

        {collected !== undefined && (
          <Alert
            tone={collected.destinationsWithoutMetrics.length > 0 ? 'warning' : 'success'}
            title={`Recorded ${String(collected.pointsRecorded)} measured data point${
              collected.pointsRecorded === 1 ? '' : 's'
            }`}
          >
            {collected.destinationsWithoutMetrics.length > 0 ? (
              <>
                These channels returned nothing because they expose no metrics API:{' '}
                {collected.destinationsWithoutMetrics.join(', ')}. That is a gap in what can be
                measured, not the same as no activity.
              </>
            ) : (
              <>Every destination in this workspace reported.</>
            )}
          </Alert>
        )}

        {/* The report: pick a subject, read its measured vs estimated figures. */}
        <div className="flex flex-wrap items-end gap-4 rounded-lg border border-edge bg-surface px-4 py-3">
          <Field label="Report on" htmlFor="dashboard-scope">
            <Select
              id="dashboard-scope"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value as Scope);
                setEntityId('');
              }}
            >
              {SCOPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Which one" htmlFor="dashboard-entity">
            <Select
              id="dashboard-entity"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              disabled={options.length === 0}
            >
              <option value="">Choose one…</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <p className="ml-auto max-w-[16rem] text-xs text-ink-muted">
            <strong className="text-navy-700">Measured</strong> from the channel ·{' '}
            <strong className="text-navy-700">estimated</strong> by INTEGRA. Never merged.
          </p>
        </div>

        {entityId === '' ? (
          <EmptyState
            icon={FilterIcon}
            title="Choose something to report on"
            description="Pick a campaign, channel or asset above and its measured and estimated figures appear here."
            features={[
              { icon: DashboardIcon, label: 'Report by campaign, channel or asset' },
              { icon: TrendIcon, label: 'Measured and estimated kept apart' },
              { icon: CheckCircleIcon, label: 'Provenance shown, never a vanity number' },
            ]}
          />
        ) : (
          <QueryState loading={dashboard.isLoading} error={dashboard.error} what="the report">
            {metrics.length === 0 ? (
              <EmptyState
                icon={DashboardIcon}
                title="No metrics for this period"
                description="Nothing measured or estimated for this selection in the window. Run a collection, or check the channel exposes a metrics API."
                features={[
                  { icon: TrendIcon, label: 'Daily trend once data lands' },
                  { icon: ConnectorIcon, label: 'Pulls from every connected channel' },
                  { icon: CheckCircleIcon, label: 'Absent readings stated, not zeroed' },
                ]}
                action={
                  <Button onClick={() => collect.mutate()} loading={collect.isPending}>
                    Collect now
                  </Button>
                }
              />
            ) : (
              <Stack gap="md">
                {dashboard.data !== undefined && (
                  <p className="text-xs text-ink-muted">
                    Window {new Date(dashboard.data.window.from).toLocaleDateString()} –{' '}
                    {new Date(dashboard.data.window.to).toLocaleDateString()}
                  </p>
                )}
                {metrics.map((rollup) => (
                  <MetricRollupCard
                    key={rollup.metric}
                    rollup={rollup}
                    series={dashboard.data?.series ?? []}
                    onInspect={() => setInspecting(rollup)}
                  />
                ))}
              </Stack>
            )}
          </QueryState>
        )}

        {/* Website & audience: a source's OWN measured data (GA4 property-level),
            surfaced beside channel data but never blended into it (§2.7). */}
        <Stack gap="md">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-edge pt-6">
            <div>
              <h2 className="font-heading text-lg text-navy-700">Website &amp; audience</h2>
              <p className="text-xs text-ink-muted">
                Site-wide figures a data source measured (e.g. a GA4 property). Shown apart from the
                per-channel report above — a website session is not a channel post&rsquo;s
                measurement.
              </p>
            </div>
            {site.data !== undefined && (
              <p className="text-xs text-ink-muted">
                Window {new Date(site.data.window.from).toLocaleDateString()} –{' '}
                {new Date(site.data.window.to).toLocaleDateString()}
              </p>
            )}
          </div>
          <QueryState loading={site.isLoading} error={site.error} what="site analytics">
            {siteMetrics.length === 0 ? (
              <EmptyState
                icon={GlobeIcon}
                title="No site analytics yet"
                description="Connect a source like Google Analytics 4, choose a property, then run a collection to see its measured figures here."
                features={[
                  { icon: ConnectorIcon, label: 'Connect Google Analytics 4' },
                  { icon: TrendIcon, label: 'Site-wide sessions, users and views' },
                  { icon: CheckCircleIcon, label: 'Kept apart from channel data' },
                ]}
                action={
                  <Button onClick={() => collect.mutate()} loading={collect.isPending}>
                    Collect now
                  </Button>
                }
              />
            ) : (
              <Stack gap="md">
                {siteMetrics.map((rollup) => (
                  <SourceMetricCard
                    key={`${rollup.source}:${rollup.externalId ?? ''}:${rollup.metric}`}
                    rollup={rollup}
                  />
                ))}
              </Stack>
            )}
          </QueryState>
        </Stack>
      </Stack>

      <Inspector
        open={inspecting !== null}
        title={inspecting !== null ? inspecting.metric.replace(/_/g, ' ') : ''}
        subtitle="Provenance for this metric — measured and estimated are kept apart."
        onClose={() => setInspecting(null)}
        fields={
          inspecting === null
            ? []
            : [
                {
                  label: 'Measured',
                  provenance: 'measured',
                  value:
                    inspecting.measured !== null
                      ? formatNumber(inspecting.measured)
                      : 'No channel measurement for this period (an absence of data, not zero).',
                },
                {
                  label: 'Measured by',
                  value:
                    inspecting.measuredSources.length > 0
                      ? inspecting.measuredSources.join(', ')
                      : 'No channel reported this metric.',
                },
                {
                  label: 'Estimated',
                  provenance: 'estimated',
                  value:
                    inspecting.estimated !== null
                      ? `${formatNumber(inspecting.estimated)} — computed by INTEGRA, not a channel reading.`
                      : 'No estimate computed for this period.',
                },
                {
                  label: 'Window',
                  value:
                    dashboard.data !== undefined
                      ? `${new Date(dashboard.data.window.from).toLocaleDateString()} – ${new Date(dashboard.data.window.to).toLocaleDateString()}`
                      : 'Unknown',
                },
                { label: 'Note', value: inspecting.note ?? 'None.' },
              ]
        }
      />
    </Page>
  );
}
